import React, { useMemo, useState } from "react";
import {
  Paper, Box, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, IconButton, Tooltip, Chip, TablePagination, CircularProgress,
  Typography, Button, Switch, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GetApp from "@mui/icons-material/GetApp";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";

import {
  ECOM_CONTENT_TYPE_LABELS,
  getEcomContentTypeLabel,
} from "../constants/ecomContentTypes";

const TYPE_LABEL = ECOM_CONTENT_TYPE_LABELS;

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
};

const ContentTable = ({
  contents = [],
  rowCount = 0,
  page = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onEditContent,
  onDeleteContent,
  onToggleStatus,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
}) => {
  const [localSearch, setLocalSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const ordered = useMemo(
    () =>
      contents
        .slice()
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0) || String(a.titulo).localeCompare(String(b.titulo))),
    [contents]
  );

  const selectedArr = Array.from(selected);
  const allOnPageSelected = ordered.length > 0 && ordered.every((x) => selected.has(x.id));
  const isRowSelected = (id) => selected.has(id);

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const ids = ordered.map((x) => x.id);
      const next = new Set(prev);
      if (allOnPageSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });

  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const exportColumns = [
    { field: "ordem", headerName: "Ordem" },
    { field: "tipo", headerName: "Tipo" },
    { field: "titulo", headerName: "Título" },
    { field: "slug", headerName: "Slug" },
    { field: "status", headerName: "Status" },
  ];
  const selectedData = useMemo(
    () =>
      ordered
        .filter((c) => selected.has(c.id))
        .map((c) => ({
          ordem: String(c.ordem ?? ""),
          tipo: TYPE_LABEL[c.tipo] || getEcomContentTypeLabel(c.tipo) || c.tipo || "—",
          titulo: c.titulo || "—",
          slug: c.slug || "—",
          status: c.status ? "Ativo" : "Inativo",
        })),
    [ordered, selected]
  );
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Conteúdos Selecionados", 20, 10);
    const headers = exportColumns.map((c) => c.headerName);
    const body = selectedData.map((row) => exportColumns.map((col) => row[col.field]));
    doc.autoTable({ head: [headers], body });
    doc.save("conteudos.pdf");
  };
  const csvData = useMemo(
    () =>
      selectedData.map((row) => {
        const o = {};
        exportColumns.forEach((c) => (o[c.headerName] = row[c.field]));
        return o;
      }),
    [selectedData]
  );

  return (
    <Paper variant="outlined">
      {/* Busca */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Buscar"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Ações em massa padronizadas */}
      {selectedArr.length > 0 && (
        <Box sx={{ mx: 1.5, mb: 2, py: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="subtitle1" color="text.secondary">
            {selectedArr.length} registro(s) selecionado(s)
          </Typography>
          <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={exportPDF} sx={{ textTransform: "uppercase" }}>
            Exportar PDF
          </Button>
          <Button variant="contained" color="primary" startIcon={<GetApp />} sx={{ textTransform: "uppercase" }}>
            <CSVLink data={csvData} filename="conteudos.csv" style={{ textDecoration: "none", color: "inherit" }}>
              Exportar CSV
            </CSVLink>
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setConfirmBulkDelete(true)}
            sx={{ textTransform: "uppercase" }}
          >
            EXCLUIR SELECIONADOS ({selectedArr.length})
          </Button>
        </Box>
      )}

      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
        <DialogTitle>Excluir selecionados</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir {selectedArr.length} registro(s) selecionado(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)}>Cancelar</Button>
          <Button
            color="error"
            onClick={() => {
              onBulkDelete?.(selectedArr);
              setConfirmBulkDelete(false);
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={!allOnPageSelected && selectedArr.length > 0}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Ordem</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Atualizado</TableCell>
              <TableCell>TOC</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box sx={{ py: 3, display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Carregando…</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : ordered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">Nenhum conteúdo encontrado.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              ordered.map((c) => {
                const checked = isRowSelected(c.id);
                return (
                  <TableRow key={c.id} hover selected={checked}>
                    <TableCell>
                      <Checkbox checked={checked} onChange={() => toggleSelect(c.id)} />
                    </TableCell>
                    <TableCell>{c.ordem ?? ""}</TableCell>
                    <TableCell>
                      <Chip size="small" label={TYPE_LABEL[c.tipo] || getEcomContentTypeLabel(c.tipo) || c.tipo} />
                    </TableCell>
                    <TableCell>{c.titulo || "—"}</TableCell>
                    <TableCell>{c.slug || "—"}</TableCell>
                    <TableCell>{fmtDate(c.updated_at)}</TableCell>
                    <TableCell>{c.auto_toc ? <Chip size="small" label="TOC" variant="outlined" /> : ""}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch size="small" checked={!!c.status} onChange={() => onToggleStatus?.(c)} />
                        <Chip
                          size="small"
                          label={c.status ? "Ativo" : "Inativo"}
                          color={c.status ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => onEditContent?.(c)}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => onDeleteContent?.(c.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: 1 }}>
        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Paper>
  );
};

export default ContentTable;
